import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Drawer,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useCallback, useEffect, useMemo, useState } from "react";
import { foyerApi } from "../../http/foyer";
import type { Asset } from "@shared/types/content-api";
import {
  imageSrc,
  resolveChannelLogoImage,
  resolveVodThumbnail,
} from "@shared/vod-images";
import { MIN_SEARCH_LENGTH } from "@shared/asset-search";
import { saveErrorText } from "./findings";
import { friendlyError } from "../../utils/apiError";
import { useTranslation } from "react-i18next";
import { translateKey, type Translate } from "../../i18n/translate";
import { tokens } from "../../theme";

/**
 * "Pick films & series" — the media picker behind a curated carousel (BE-28).
 *
 * ## It reads the CATALOGUE, not Foyer's media store
 *
 * The same distinction BE-15 hit with the artwork picker, and it is worth
 * stating twice because the two stores look interchangeable in a UI and are
 * not. `assetIds` on a carousel are **Content API** ids; Foyer's `assetStore`
 * holds uploaded files for signage and templates. Offering a Foyer asset here
 * would produce a rail that draws in the CMS and shows nothing on any
 * television.
 *
 * ## Multi-select, and the order is the order you pick them in
 *
 * The design draws a radio, but a carousel takes a LIST — and array position is
 * the display order. So this is a checkbox, and it appends: the operator's
 * click order becomes the rail's order, which is the only mapping that does not
 * need explaining. Deselecting removes without disturbing the rest.
 *
 * ## Searching under three characters is withheld, not sent
 *
 * Upstream refuses a shorter plain-text search, and the FULLTEXT index does not
 * hold two-letter words at all — it would answer "nothing matches" rather than
 * "too short". Typing `TV` must not look like an empty catalogue.
 *
 * ## The whole catalogue is reachable — it was not
 *
 * 🚨 Until this change the drawer read `page: 1` and nothing else. It printed
 * the server's own total ("55 items") beside a list that could never hold more
 * than {@link PAGE_SIZE} of them, so a curated rail could only ever be built
 * from whatever the catalogue happened to return first. That is not a paging
 * nicety — it made most of the library **unpickable**, silently, while telling
 * the operator how much they were missing.
 *
 * Later pages APPEND. `pagination.next` is authoritative rather than a total
 * ÷ page-size guess, because the catalogue is a service we do not own and its
 * page size is a ceiling, not a promise (see [[distribution-search-and-sort]]).
 *
 * ## 🚨 It opens OVER a dialog, and MUI will not do that by default
 *
 * Its only caller is the carousel editor, which is a `Dialog`. Both portal to
 * `<body>`, so which one a click reaches is decided entirely by z-index — and
 * MUI's defaults are `drawer: 1200` against `modal: 1300`. Left alone, this
 * drawer renders UNDERNEATH the dialog's backdrop: visibly dimmed, and inert to
 * every click, with nothing in the console to explain either.
 *
 * The lift lives here rather than at the call site because it is a fact about
 * what this component is FOR. A picker exists to be opened from an editor, and
 * an editor is a dialog; a caller that had to remember to raise it would be a
 * caller that eventually forgets.
 *
 * Focus needs no such help — MUI's modal manager tracks mount order, and the
 * dialog's focus trap stands down on its own once a newer modal is on top.
 * Stacking was the whole defect.
 *
 * ## No sort control, on purpose
 *
 * The drawer shows the catalogue's own newest-first order, which is the Figma
 * mock's "Senast tillagda". `needsCompleteSweep()` is why there is no dropdown
 * beside it: `created desc` and, while searching, `relevance` are the ONLY
 * orders upstream emits unaided. Every other one — `title`, or even `created`
 * ASCENDING — makes Foyer read *every matching row* of a shared production
 * catalogue before it can draw the first page. That is a real cost to hang off
 * a picker drawer, and the VOD titles page is where it is already paid and
 * already labelled. The order is stated here instead, so the absence reads as
 * a decision rather than an omission.
 */

export interface CataloguePickerDrawerProps {
  open: boolean;
  /** Already-picked ids, in their authored order. */
  value: string[];
  onClose: () => void;
  onConfirm: (assetIds: string[]) => void;
}

type TabKey = "channels" | "movies" | "series";

/**
 * One tab per KIND, and nothing that spans them.
 *
 * There was a combined "Films & series" tab first. It had to go for two
 * reasons, and only one of them is cosmetic:
 *
 *  - **It could not include channels.** `source` is not a preference:
 *    `GET /distribution/vod-titles` merges its own `type` base LAST precisely
 *    so a querystring cannot widen it, so nothing arriving through that route
 *    is ever a channel. A combined tab was therefore combined over two of the
 *    three kinds, which is the shape of half-truth this drawer keeps producing.
 *  - **Its label was the longest thing here** and squeezed "Channels" into an
 *    ellipsis in a 420px drawer. A tab you cannot read the name of is a tab
 *    nobody clicks.
 *
 * Ruled by Sigge: channels, then films, then series. Channels lead because
 * they are what the estate actually sells — every row in the design's own
 * overview mock is a channel carousel — and the first tab is what opens.
 *
 * A pick is not scoped to a tab, so a rail mixing all three is still one
 * carousel; the tabs decide what you are BROWSING, never what you may keep.
 */
const TABS: {
  key: TabKey;
  source: "vod" | "channel";
  type?: ("movie" | "series")[];
}[] = [
  { key: "channels", source: "channel" },
  { key: "movies", source: "vod", type: ["movie"] },
  { key: "series", source: "vod", type: ["series"] },
];

const PAGE_SIZE = 30;

/**
 * Append a page, dropping anything already in hand.
 *
 * The catalogue is live: a title ingested between two reads shifts every later
 * page down by one, and the same row then arrives twice. Rendered, that is a
 * duplicate React key — but the worse half is silent: `toggle()` is a
 * `includes`/`filter` pair, so a duplicated row would show as picked in one
 * place and unpicked in the other. First occurrence wins, so the position the
 * operator has already scrolled past does not move under them.
 */
function mergeById(prev: Asset[], next: Asset[]): Asset[] {
  const seen = new Set(prev.map((r) => r.assetId));
  return [...prev, ...next.filter((r) => !seen.has(r.assetId))];
}

/**
 * A row's own word for what it is. `type` is the catalogue's, and the four VOD
 * values do not all have a natural label here — a picker only ever lists films,
 * series and channels, so anything else falls back to the raw type rather than
 * being renamed into one of them.
 */
export function assetKindLabel(t: Translate, row: Pick<Asset, "type">): string {
  if (row.type === "channel" || row.type === "series" || row.type === "movie")
    return translateKey(t, `content:cataloguePickerDrawer.kind.${row.type}`);
  return row.type;
}

/**
 * 🚨 A channel resolves through a DIFFERENT image order, and this is not a
 * detail.
 *
 * `THUMBNAIL_TYPE_PREFERENCE` puts `logo` last, because a transparent mark
 * reads as a broken image on a VOD tile. Run a channel through it and you get
 * its promo still instead — a white 16:9 frame with a small mark somewhere in
 * it — so a channel list becomes rows of near-identical white rectangles. That
 * exact mistake has already been made once and reported as "this selector looks
 * weird"; `CHANNEL_LOGO_TYPE_PREFERENCE` exists because of it.
 *
 * Same fallback either way: null is a real answer, and the row draws its own
 * empty box rather than an <img> that will fail.
 */
export function pickerThumbnail(row: Asset): string | null {
  const input = {
    images: row.images ?? [],
    language: "sv",
    languages: ["sv", "en"],
  };
  if (row.type === "channel") {
    const image = resolveChannelLogoImage(input);
    // `contain`-sized rather than cropped: a logo cropped to fill is a logo
    // with its edges cut off.
    return image ? imageSrc(image, { width: 112, height: 68 }) : null;
  }
  return resolveVodThumbnail(input, { width: 112, height: 68 });
}

export default function CataloguePickerDrawer({
  open,
  value,
  onClose,
  onConfirm,
}: CataloguePickerDrawerProps) {
  const { t } = useTranslation(["content", "common"]);
  // Seeded once from `value` and never prop-watched. The drawer is remounted by
  // key from the dialog, so a reopen is a fresh mount — the [[cms-list-and-editor]]
  // rule, and the reason there is no "did the value change?" effect here.
  const [picked, setPicked] = useState<string[]>(() => value);
  const [tab, setTab] = useState<TabKey>("channels");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState<Asset[]>([]);
  const [total, setTotal] = useState(0);
  /** `pagination.next`, straight from the catalogue. `null` = that was the end. */
  const [nextPage, setNextPage] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  /**
   * Kept apart from {@link loading} so "show me more" does not blank the list
   * the operator is mid-way through picking from.
   */
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Which page the failed read was asking for, so Retry asks for the SAME one.
   * Without it a failed page 7 retries as page 1 and silently discards six
   * pages of scrolling.
   */
  const [retryPage, setRetryPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(query.trim().length >= MIN_SEARCH_LENGTH ? query.trim() : "");
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const load = useCallback(
    async (pageNumber: number) => {
      const more = pageNumber > 1;
      if (more) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const spec = TABS.find((candidate) => candidate.key === tab);
        const query = {
          page: pageNumber,
          pageSize: PAGE_SIZE,
          // Explicit, because a search with the default `created desc` costs a
          // full sweep upstream while `relevance` is free. See
          // [[distribution-search-and-sort]].
          ...(search
            ? { search, sort: "relevance" as const, order: "desc" as const }
            : {}),
        };
        // Both routes answer the same envelope — a row array, `pagination` and
        // `sort` — under a different key, so the only thing that branches here
        // is which call is made and which key it lands under.
        const page =
          spec?.source === "channel"
            ? await foyerApi.listDistributionChannels(query).then((p) => ({
                rows: p.channels ?? [],
                pagination: p.pagination,
              }))
            : await foyerApi
                .listVodTitles({ ...query, type: spec?.type })
                .then((p) => ({
                  rows: p.titles ?? [],
                  pagination: p.pagination,
                }));
        const fetched: Asset[] = page.rows;
        setRows((prev) => (more ? mergeById(prev, fetched) : fetched));
        setTotal(page.pagination?.total ?? fetched.length);
        setNextPage(page.pagination?.next ?? null);
      } catch (e) {
        const err = e as { code?: string };
        setError(saveErrorText(t, err.code, friendlyError(t, e)));
        setRetryPage(pageNumber);
        // A failed LATER page keeps what is already on screen — the operator's
        // picks live in `picked`, but the list they were reading is context,
        // and throwing it away over one bad request is its own small data loss.
        if (!more) {
          setRows([]);
          setTotal(0);
        }
        setNextPage(null);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [tab, search, t]
  );

  // `load` changes identity when the tab or the search does, and that IS the
  // reset: a new query always starts at page 1.
  useEffect(() => {
    if (open) void load(1);
  }, [open, load]);

  const pickedSet = useMemo(() => new Set(picked), [picked]);

  const toggle = (assetId: string) => {
    setPicked((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : // Append. Click order IS display order.
          [...prev, assetId]
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      // Above the modal layer, not above the drawer layer. See the docblock:
      // the caller is a Dialog, and MUI's default drawer z-index sits below it.
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
      PaperProps={{ sx: { width: { xs: "100%", sm: 420 }, p: 0 } }}
    >
      <Stack sx={{ height: "100%" }}>
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "fontWeightBold", mb: 2 }}>
            {t("cataloguePickerDrawer.title")}
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={t("cataloguePickerDrawer.searchPlaceholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              "aria-label": t("cataloguePickerDrawer.searchLabel"),
            }}
          />
        </Box>

        <Tabs
          value={tab}
          onChange={(_, next: TabKey) => setTab(next)}
          // Even thirds. Three short labels fit a 420px drawer with room to
          // spare, and sharing the width equally is what stops a longer word in
          // one of them from ever squeezing its neighbours into an ellipsis
          // again.
          variant="fullWidth"
          sx={{ px: 3, minHeight: 40 }}
        >
          {TABS.map((spec) => (
            <Tab
              key={spec.key}
              value={spec.key}
              label={t(`cataloguePickerDrawer.tabs.${spec.key}`)}
              sx={{ minHeight: 40 }}
            />
          ))}
        </Tabs>
        <Divider />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, py: 1.5 }}
        >
          <Typography variant="body2" sx={{ color: tokens.inkFaint }}>
            {/* The server's own total, never a count of the page in hand —
                counting a page is the exact lie this whole model was corrected
                to stop making. What IS counted separately is how much of it is
                on screen, because the gap between the two is the thing the
                operator has to act on. */}
            {rows.length < total
              ? t("cataloguePickerDrawer.countOfTotal", {
                  shown: rows.length,
                  total,
                })
              : t("cataloguePickerDrawer.countTotal", { total })}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkFaint }}>
            {t("cataloguePickerDrawer.picked", { count: picked.length })}
          </Typography>
        </Stack>
        <Divider />

        <Box sx={{ flex: 1, overflowY: "auto" }}>
          {loading && (
            <Stack alignItems="center" sx={{ py: 6 }}>
              <CircularProgress size={22} />
            </Stack>
          )}
          {/* Never an empty list on a failure: "the catalogue is down" must not
              read as "there is no content". A FIRST-page failure has no list to
              stand beside, so it takes the space the list would have had. */}
          {!loading && error && rows.length === 0 && (
            <Stack spacing={1.5} alignItems="flex-start" sx={{ p: 3 }}>
              <Typography variant="body2" sx={{ color: tokens.error }}>
                {error}
              </Typography>
              <Button size="small" onClick={() => void load(retryPage)}>
                {t("cataloguePickerDrawer.retry")}
              </Button>
            </Stack>
          )}
          {!loading && !error && rows.length === 0 && (
            <Typography variant="body2" sx={{ p: 3, color: tokens.inkFaint }}>
              {search
                ? t("cataloguePickerDrawer.noMatch")
                : t("cataloguePickerDrawer.emptyHere")}
            </Typography>
          )}
          {/* Rows survive a LATER page's failure: the error sits BELOW them
              rather than replacing them. Throwing away the list an operator was
              reading, because the page after it did not arrive, is its own
              small data loss. */}
          {!loading &&
            rows.map((row) => {
              const thumb = pickerThumbnail(row);
              const checked = pickedSet.has(row.assetId);
              return (
                <Stack
                  key={row.assetId}
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  onClick={() => toggle(row.assetId)}
                  role="checkbox"
                  aria-checked={checked}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === " " || e.key === "Enter") {
                      e.preventDefault();
                      toggle(row.assetId);
                    }
                  }}
                  sx={{
                    px: 3,
                    py: 1.25,
                    cursor: "pointer",
                    borderBottom: `1px solid ${tokens.divider}`,
                    "&:hover": { background: tokens.rowAlt },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 34,
                      borderRadius: 1,
                      overflow: "hidden",
                      flexShrink: 0,
                      background: tokens.rowAlt,
                    }}
                  >
                    {/* Null is a real answer: a generated tile reads as empty
                        state, a broken <img> reads as a bug. */}
                    {thumb && (
                      <Box
                        component="img"
                        src={thumb}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          // A logo is CONTAINED, not cropped. `cover` on a
                          // channel mark cuts its edges off, which is the
                          // second half of the same mistake as resolving it
                          // through the VOD image order.
                          objectFit:
                            row.type === "channel" ? "contain" : "cover",
                        }}
                      />
                    )}
                  </Box>
                  <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: "fontWeightSemibold" }}
                      noWrap
                      title={row.originalTitle}
                    >
                      {row.originalTitle || row.assetId}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: tokens.inkFaint }}
                    >
                      {assetKindLabel(t, row)}
                      {row.productionYear ? ` · ${row.productionYear}` : ""}
                    </Typography>
                  </Stack>
                  <Checkbox
                    checked={checked}
                    tabIndex={-1}
                    aria-hidden
                    sx={{ pointerEvents: "none" }}
                  />
                </Stack>
              );
            })}

          {/* Driven by the catalogue's own `next`, not by `rows.length < total`:
              a total is a count of matches and a `next` is a promise there is
              another page. They disagree whenever the set changes mid-read, and
              only one of them is answering the question the button asks. */}
          {!loading && error && rows.length > 0 && (
            <Stack spacing={1} alignItems="flex-start" sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" sx={{ color: tokens.error }}>
                {error}
              </Typography>
              <Button size="small" onClick={() => void load(retryPage)}>
                {t("cataloguePickerDrawer.retry")}
              </Button>
            </Stack>
          )}

          {!loading && !error && nextPage !== null && (
            <Stack alignItems="center" sx={{ py: 2 }}>
              <Button
                size="small"
                disabled={loadingMore}
                onClick={() => void load(nextPage)}
                startIcon={
                  loadingMore ? <CircularProgress size={14} /> : undefined
                }
              >
                {loadingMore
                  ? t("common:states.loading")
                  : t("cataloguePickerDrawer.showMore")}
              </Button>
            </Stack>
          )}
        </Box>

        <Divider />
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ p: 3 }}
          justifyContent="flex-end"
        >
          <Button onClick={onClose}>{t("common:buttons.cancel")}</Button>
          <Button variant="contained" onClick={() => onConfirm(picked)}>
            {t("cataloguePickerDrawer.pick")}
          </Button>
        </Stack>
      </Stack>
    </Drawer>
  );
}
